// Languages (02-building-blocks.md, "Languages"): every text from one catalog per language, English
// as the fallback; the choice from the person's setting, else the browser's languages, else English;
// Intl with the active locale; the root element's `lang` kept right. Only the active catalog loads.
// Another language is one more catalog file and one more entry in LANGUAGES.
import { useSyncExternalStore } from 'react';
import { PRODUCT } from '../core/product';
import { readSettings, writeSetting } from '../core/settings';
import { en, type Key } from './en';

export type { Key };
export type Plural = { one: string; other: string };
export type Catalog = Partial<Record<Key, string | Plural>>;

/** Each language: its locale for Intl, its own name, and its catalog. */
export const LANGUAGES = {
  en: { locale: 'en-GB', name: 'English', load: async (): Promise<Catalog> => ({}) },
  de: { locale: 'de-CH', name: 'Deutsch', load: async (): Promise<Catalog> => (await import('./de')).de },
} as const;

export type Language = keyof typeof LANGUAGES;

let language: Language = 'en';
let catalog: Catalog = {};
const listeners = new Set<() => void>();

const supported = (code: string | undefined): Language | undefined => {
  const main = code?.toLowerCase().split('-')[0];
  return main && main in LANGUAGES ? (main as Language) : undefined;
};

/** The person's setting, else the first supported browser language, else English. */
export function chosenLanguage(): Language {
  return supported(readSettings().language) ?? browserLanguage();
}

/** The browser's own choice (for "Follow the browser (…)"). */
export function browserLanguage(): Language {
  return navigator.languages.map(supported).find(Boolean) ?? 'en';
}

/** Loads a language's catalog and applies it at once, everywhere (no reload). */
export async function applyLanguage(next: Language = chosenLanguage()): Promise<void> {
  catalog = await LANGUAGES[next].load();
  language = next;
  document.documentElement.lang = next;
  listeners.forEach((l) => l());
}

/** The person's choice (the sign-in switch, Settings → General): kept, and applied at once. */
export async function chooseLanguage(next: Language | undefined): Promise<void> {
  writeSetting('language', next);
  await applyLanguage(next ?? browserLanguage());
}

export const currentLanguage = () => language;
export const onLanguage = (l: () => void) => (listeners.add(l), () => void listeners.delete(l));
export const locale = () => LANGUAGES[language].locale;

/** Re-renders when the language changes (used once, at the top). */
export const useLanguage = () =>
  useSyncExternalStore(
    (l) => (listeners.add(l), () => listeners.delete(l)),
    () => language,
  );

/**
 * A text in the active language, English when the catalog lacks it. `{name}` placeholders are
 * filled from `params` ({product} always); a text with forms picks one by `params.n`.
 */
export function t(key: Key, params: Record<string, string | number> = {}): string {
  const value = catalog[key] ?? en[key];
  const text = typeof value === 'string' ? value : value[new Intl.PluralRules(locale()).select(Number(params.n)) === 'one' ? 'one' : 'other'];
  return text.replace(/\{(\w+)\}/g, (all, name: string) => (name === 'product' ? PRODUCT : name in params ? (name === 'n' && typeof params.n === 'number' ? params.n.toLocaleString(locale()) : String(params[name])) : all));
}
